// cSpell:enableCompoundWords
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NFL_TEAMS_TOTAL, TeamAbrvToTeamID, TeamNamesByID } from '@shared/constants';
import { breakSeasonWeekID, breakUID, getPrefixForOrderedUID, toNumber } from '@shared/helpers';
import { BYE, ITeamDetails, ITeamsOnByeWeek, Maybe, SeasonID, TeamAbrvType, TeamID, UID } from '@shared/types';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { ActionCls, Cls, Emojis, LocationCls, ValueCls } from 'app/constants';
import jsonTeamInfos from 'app/fetchapi/teamInfos.json';
import { getShouldFetchTeamInfo } from 'app/slices/SyncManagerSlice';
import { UserAuthState } from 'app/slices/UserSlice';
import { sortTeamDetailsByTeamID } from 'helpers/SortHelpers';
import { getIndexDBLoaderWebWorker, getLoadStoreRequestMessage } from 'helpers/StoreHelpers';
import { calculateTeamsOnByeWeek, getAllTeamsAllAvailableSeasonsByeWeeks } from 'helpers/TeamHelpers';
import { updateRecordsInIDBDatabase } from 'lib/idb/IndexDbInterface';
import { memoize } from 'proxy-memoize';
import {
  AppThunk,
  IWorkerIDBLoaderResponseMessage,
  LeagueProps,
  NFLDivision,
  RootState,
  Stores,
  StoresLoadedState,
  UIDSchedulePayload,
} from 'types';
import { getSelectedSeasonWeek, getSelectedTeamName, getSelectedWeek, getSelectedYear } from './GamesSlice';

const initialState: LeagueProps = {
  teams: [],
  afcConference: [],
  nfcConference: [],
  afcNorth: [],
  afcEast: [],
  afcWest: [],
  afcSouth: [],
  nfcNorth: [],
  nfcEast: [],
  nfcWest: [],
  nfcSouth: [],
  loaded: false,
  byeWeekTeams: [],
  seasonYear: 2026,
  storesLoadedState: {
    teamsInfoLoaded: false,
    byeWeekTeamsLoaded: false,
  },
};

const CONSOLE_ON = getConsoleState(ConsoleID.LeagueSlice);

export const populateLeagueTeamInfos = (): AppThunk => (dispatch) => {
  const teams = jsonTeamInfos.teams as ITeamDetails[];
  dispatch(setTeams(teams.sort(sortTeamDetailsByTeamID)));
  dispatch(syncLeagueReduxWithIDB());

  // generate bye week teams
  const byeWeekTeams: ITeamsOnByeWeek[] = getAllTeamsAllAvailableSeasonsByeWeeks(teams);
  console.log('++++++++++++++++++++++++++++ byeWeekTeams', byeWeekTeams);
  dispatch(setByeWeeks(byeWeekTeams));
  const msg = `${Emojis.eyes} %c populateLeagueTeamInfos %c %c LOADING %c divisions and conferences`;
  console.log(msg, LocationCls, '', ActionCls, '');

  // dispatch(setAfcConference(teams.filter((team) => team.teamConference === 'AFC')));
  // dispatch(setNfcConference(teams.filter((team) => team.teamConference === 'NFC')));
  // dispatch(setAfcNorth(teams.filter((team) => team.teamDivision === NFLDivision.AFCNorth)));
  // dispatch(setAfcEast(teams.filter((team) => team.teamDivision === NFLDivision.AFCEast)));
  // dispatch(setAfcWest(teams.filter((team) => team.teamDivision === NFLDivision.AFCWest)));
  // dispatch(setAfcSouth(teams.filter((team) => team.teamDivision === NFLDivision.AFCSouth)));
  // dispatch(setNfcNorth(teams.filter((team) => team.teamDivision === NFLDivision.NFCNorth)));
  // dispatch(setNfcEast(teams.filter((team) => team.teamDivision === NFLDivision.NFCEast)));
  // dispatch(setNfcWest(teams.filter((team) => team.teamDivision === NFLDivision.NFCWest)));
  // dispatch(setNfcSouth(teams.filter((team) => team.teamDivision === NFLDivision.NFCSouth)));
};

// export const populateLeagueTeams = (): AppThunk => (dispatch, getState) => {
//   if (getState().league.loaded) return;

//   const localTeams: ITeamDetails[] = [];
//   const keys = Object.keys(TeamAbrvToTeamID) as (keyof typeof TeamAbrvToTeamID)[];
//   keys.forEach((key) => {
//     localTeams.push({
//       teamID: TeamAbrvToTeamID[key],
//       teamName: key,
//       teamFullName: '',
//       teamLogo: '',
//       teamColor: '',
//       teamSecondaryColor: '',
//       teamCity: '',
//       teamStadium: '',
//       teamDivision: NFLDivision.AFCNorth,
//       teamConference: NFLConference.AFC,
//       teamAbrv: key,
//       teamState: '',
//       teamCapacity: 0,
//       teamHeadCoach: '',
//       teamOffensiveCoordinator: '',
//       teamDefensiveCoordinator: '',
//       teamSpecialTeamsCoach: '',
//       teamOffensiveScheme: '',
//       teamDefensiveScheme: '',
//       teamBaseFormation: '',
//       teamBaseDefense: '',
//       teamBaseOffense: '',
//       teamBaseSpecialTeams: '',
//       teamRoster: [],
//       schedule: [],
//     });
//   });

//   dispatch(setTeams(localTeams.sort(sortTeamDetailsByTeamID)));
// };

const onDataAddedToIDB = (data: unknown) => {
  console.log(`Teams have been updated, data added to IDB: ${data}`);
};

export const syncLeagueReduxWithIDB = (): AppThunk => (dispatch, getState) => {
  const teams = getState().league.teams;
  updateRecordsInIDBDatabase(Stores.TeamInfo, teams, onDataAddedToIDB);
  // saveTeams(teams);
};

/** @deprecated */
export const doUpdateTeamSchedule =
  (uID: UID, teamID: TeamID): AppThunk =>
  (dispatch, getState) => {
    const teams: ITeamDetails[] = getState().league.teams;
    const seasonYear = Number(breakUID(uID).yearID);
    const positionIndex = teams.findIndex((team) => team.teamID === teamID);
    const team: ITeamDetails = teams[positionIndex] as ITeamDetails;
    const teamSchedule = team.scheduleByYear[seasonYear] ?? [];
    const listWithPossibleDupes: UID[] = [...teamSchedule, uID];
    const schedule = Array.from(new Set(listWithPossibleDupes));

    dispatch(setSchedule({ positionIndex, schedule, seasonYear }));
  };

export const doSetLeagueTeams =
  (teams: ITeamDetails[]): AppThunk =>
  (dispatch) => {
    teams.sort(sortTeamDetailsByTeamID);
    console.log(`Setting teams in league slice: ${teams.length}`);
    dispatch(setTeams(teams));
  };

export const updateTeamDetailScheduleUID = (): AppThunk => (dispatch, getState) => {
  const teams = getState().league.teams;
  const updatedTeams: ITeamDetails[] = teams.map((team) => {
    const schedule = team.schedule;
    const updatedSchedule = schedule.map((uid) => {
      const [weekID, teamID] = uid.split('-');
      const prefixUID = getPrefixForOrderedUID(weekID, teamID);
      const newUID = `${prefixUID}-${uid}`;
      return newUID;
    });
    return { ...team, schedule: updatedSchedule };

    // dispatch(setSchedule({ positionIndex: team.teamID, schedule: updatedSchedule }));
  });
  updatedTeams.sort(sortTeamDetailsByTeamID);
  dispatch(setTeams(updatedTeams));
};

// export const updateTeamsScheduleStructure = (): AppThunk => (dispatch, getState) => {
//   const teams = getState().league.teams;
//   const updatedTeams: ITeamDetails[] = teams.map((team) => {
//     const scheduleByYear: TeamScheduleBySeasonYear = {
//       '2024': team.schedule,
//       '2025': [],
//     };
//     return { ...team, scheduleByYear };
//   });

//   const updateReduxStore = () => dispatch(setTeams(updatedTeams));

//   saveTeamDetails(updatedTeams).then(() => {
//     updateRecordsInIDBDatabase(Stores.TeamInfo, updatedTeams, updateReduxStore);
//   });
// };

export const doProcessLoadedTeamsOnByeWeekFromIDB =
  (teams: ITeamsOnByeWeek[]): AppThunk =>
  (dispatch) => {
    dispatch(setByeWeeks(teams));
  };

export const loadNFLTeamDetailsFromInboxDB = (): AppThunk => (dispatch, getState) => {
  if (getState().user.authState !== UserAuthState.LoggedIn) return;

  if (typeof window !== 'undefined' && window.Worker) {
    const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker('LoadTeamInfo-2024/2025');

    const processResponse = (jsonString: string) => {
      const teams: ITeamDetails[] = JSON.parse(jsonString) as ITeamDetails[];
      teams.sort(sortTeamDetailsByTeamID);
      dispatch(setTeams(teams));
    };
    const shouldFetch = getShouldFetchTeamInfo(getState());
    webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.TeamInfo, '2025', shouldFetch));
    webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
      const { type, payload } = e.data;
      const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.TeamInfo} %c %c ${type} %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
      switch (type) {
        case 'result-get-store-data':
          processResponse(payload);
          webWorkerDbLoader.terminate();
          break;

        default:
          console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
      }
    };
  }
};
// loadTeamDetailsFromDB(Stores.TeamInfo, loadedTeamDetailsCb);
// };

export const loadNFLTeamsOnByeWeekFromInboxDB = (): AppThunk => (dispatch, getState) => {
  if (getState().user.authState !== UserAuthState.LoggedIn) return;

  if (typeof window !== 'undefined' && window.Worker) {
    const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker('LoadTeamsOnByeWeek-2024/2025');

    const processResponse = (jsonString: string) => {
      const byeWeekTeams: ITeamsOnByeWeek[] = JSON.parse(jsonString) as ITeamsOnByeWeek[];
      if (byeWeekTeams.length === 36) {
        dispatch(setByeWeeks(byeWeekTeams));
      }
    };
    // else {
    //   console.warn(`${Emojis.warning} %c IDB collection is empty %c updating from redux state`, CrimsonCls, '');
    //   const teamInfos = getState().league.teams;
    //   const allByeWeeksForAllSeasons: ITeamsOnByeWeek[] = getAllTeamsAllAvailableSeasonsByeWeeks(teamInfos);
    //   dispatch(addAllNFLTeamsOnByeWeekToInboxDB([...allByeWeeksForAllSeasons]));
    // }
    //   dispatch(setTeams(teams));
    // };
    // TODO: @Andrey11 fix this, determine if we need to fetch from IDB or not
    const shouldFetch = false;
    webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.TeamsOnByeWeek, '2025', shouldFetch));
    webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
      const { type, payload } = e.data;
      const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.TeamsOnByeWeek} %c %c ${type} %c`;
      if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
      if (type === 'result-get-store-data') {
        processResponse(payload);
        webWorkerDbLoader.terminate();
      } else {
        console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
      }
    };
  }
};

// export const loadNFLTeamsOnByeWeekFromInboxDB = (): AppThunk => (dispatch, getState) => {
//   const loadedTeamsOnByeWeekCb = (byeWeekTeams: ITeamsOnByeWeek[]) => {
//     if (byeWeekTeams.length === 36) {
//       dispatch(setByeWeeks(byeWeekTeams));
//     } else {
//       console.warn(`${Emojis.warning} %c IDB collection is empty %c updating from redux state`, CrimsonCls, '');
//       const teamInfos = getState().league.teams;
//       const allByeWeeksForAllSeasons: ITeamsOnByeWeek[] = getAllTeamsAllAvailableSeasonsByeWeeks(teamInfos);
//       dispatch(addAllNFLTeamsOnByeWeekToInboxDB([...allByeWeeksForAllSeasons]));
//     }
//   };
//   loadTeamsOnByeWeekFromDB(Stores.TeamsOnByeWeek, loadedTeamsOnByeWeekCb);
// };

export const addAllNFLTeamsOnByeWeekToInboxDB =
  (teams: ITeamsOnByeWeek[]): AppThunk =>
  (dispatch) => {
    const addedTeamsOnByeWeekCb = (byeWeekTeams: ITeamsOnByeWeek[]) => {
      if (byeWeekTeams.length > 0) {
        dispatch(setByeWeeks(byeWeekTeams));
      }
    };
    updateRecordsInIDBDatabase(Stores.TeamsOnByeWeek, teams, addedTeamsOnByeWeekCb);
  };

export const addSingleWeekNFLTeamsOnByeWeekToInboxDB =
  (teams: ITeamsOnByeWeek): AppThunk =>
  (dispatch) => {
    const addedTeamsOnByeWeekCb = (byeWeekTeams: Maybe<ITeamsOnByeWeek>) => {
      if (byeWeekTeams) dispatch(updateByeWeek(byeWeekTeams));
    };
    updateRecordsInIDBDatabase(Stores.TeamsOnByeWeek, teams, addedTeamsOnByeWeekCb);
  };

export const updateReduxWithByeWeekTeams =
  (teams: ITeamsOnByeWeek[]): AppThunk =>
  (dispatch) => {
    dispatch(setByeWeeks(teams));
    dispatch(setStoresLoadedState({ byeWeekTeamsLoaded: true }));
  };

export const manualAddedForTeamsOnByeWeek = (): AppThunk => (dispatch, getState) => {
  const selectedWeekID = getState().nfl.weekSelected;
  const games = getState().schedule.games.filter((game) => game.gameWeek.replace(' ', '') === selectedWeekID);
  if (!selectedWeekID) {
    console.log('No week selected');
    return;
  }
  const weeklyTeamIDsOnBye: ITeamsOnByeWeek = calculateTeamsOnByeWeek(selectedWeekID, games);
  dispatch(addSingleWeekNFLTeamsOnByeWeekToInboxDB(weeklyTeamIDsOnBye));
};

export const debugPrintTeamInfos = (): AppThunk => (dispatch, getState) => {
  const teams = getState().league;

  console.log(`Teams in league slice: ${teams.teams.length}`);
  console.dir(teams);

  const jsonString = JSON.stringify(teams, null, 2);
  console.log(jsonString);
};

export const leagueSlice = createSlice({
  name: 'league',
  initialState,
  reducers: {
    setTeams: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.teams = action.payload;
      state.afcConference = action.payload.filter((team) => team.teamConference === 'AFC');
      state.nfcConference = action.payload.filter((team) => team.teamConference === 'NFC');
      state.afcNorth = action.payload.filter((team) => team.teamDivision === NFLDivision.AFCNorth);
      state.afcEast = action.payload.filter((team) => team.teamDivision === NFLDivision.AFCEast);
      state.afcWest = action.payload.filter((team) => team.teamDivision === NFLDivision.AFCWest);
      state.afcSouth = action.payload.filter((team) => team.teamDivision === NFLDivision.AFCSouth);
      state.nfcNorth = action.payload.filter((team) => team.teamDivision === NFLDivision.NFCNorth);
      state.nfcEast = action.payload.filter((team) => team.teamDivision === NFLDivision.NFCEast);
      state.nfcWest = action.payload.filter((team) => team.teamDivision === NFLDivision.NFCWest);
      state.nfcSouth = action.payload.filter((team) => team.teamDivision === NFLDivision.NFCSouth);

      state.storesLoadedState.teamsInfoLoaded = true;
    },
    setSchedule: (state, action: PayloadAction<UIDSchedulePayload>) => {
      const { positionIndex, schedule, seasonYear } = action.payload;
      state.teams[positionIndex].scheduleByYear[seasonYear] = schedule;
      state.storesLoadedState.teamsInfoLoaded = true;
    },
    updateTeam: (state, action: PayloadAction<ITeamDetails>) => {
      const teamIndex = state.teams.findIndex((team) => team.teamID === action.payload.teamID);
      if (teamIndex >= 0) {
        state.teams[teamIndex] = { ...state.teams[teamIndex], ...action.payload };
      }
    },
    setByeWeeksForSeason: (state, action: PayloadAction<{ seasonID: SeasonID; weeks: ITeamsOnByeWeek[] }>) => {
      state.byeWeekTeams = [
        ...state.byeWeekTeams.filter((week) => week.year !== action.payload.seasonID),
        ...action.payload.weeks,
      ];
      state.storesLoadedState.byeWeekTeamsLoaded = true;
    },
    setByeWeeks: (state, action: PayloadAction<ITeamsOnByeWeek[]>) => {
      state.byeWeekTeams = action.payload;
      state.storesLoadedState.byeWeekTeamsLoaded = true;
    },
    addByeWeek: (state, action: PayloadAction<ITeamsOnByeWeek>) => {
      state.byeWeekTeams.push(action.payload);
      state.storesLoadedState.byeWeekTeamsLoaded = true;
    },
    updateByeWeek: (state, action: PayloadAction<ITeamsOnByeWeek>) => {
      const index = state.byeWeekTeams.findIndex((byeWeek) => byeWeek.weekID === action.payload.weekID);
      if (index >= 0) {
        state.byeWeekTeams[index] = action.payload;
      }
    },
    setAfcConference: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.afcConference = action.payload;
    },
    setNfcConference: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.nfcConference = action.payload;
    },
    setAfcNorth: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.afcNorth = action.payload;
    },
    setAfcEast: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.afcEast = action.payload;
    },
    setAfcWest: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.afcWest = action.payload;
    },
    setAfcSouth: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.afcSouth = action.payload;
    },
    setNfcNorth: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.nfcNorth = action.payload;
    },
    setNfcEast: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.nfcEast = action.payload;
    },
    setNfcWest: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.nfcWest = action.payload;
    },
    setNfcSouth: (state, action: PayloadAction<ITeamDetails[]>) => {
      state.nfcSouth = action.payload;
    },
    setStoresLoadedState: (state, action: PayloadAction<Partial<StoresLoadedState>>) => {
      state.storesLoadedState = { ...state.storesLoadedState, ...action.payload };
    },
    resetTeams: () => {
      return { ...initialState };
    },
  },
});

export const {
  setTeams,
  setSchedule,
  updateTeam,
  resetTeams,
  addByeWeek, // bye week
  setByeWeeksForSeason,
  setByeWeeks, // bye week
  updateByeWeek, // bye week
  setAfcConference,
  setNfcConference,
  setAfcNorth,
  setAfcEast,
  setAfcWest,
  setAfcSouth,
  setNfcNorth,
  setNfcEast,
  setNfcWest,
  setNfcSouth,
  setStoresLoadedState,
} = leagueSlice.actions;

// #region UI Selectors *********************************************************
export const getTeams = (state: RootState): ITeamDetails[] => state.league.teams;
export const getLeagueStoreLoadedStates = (state: RootState): StoresLoadedState => state.league.storesLoadedState;
export const getLeagueTeamsLoaded = (state: RootState): boolean => state.league.storesLoadedState.teamsInfoLoaded;
export const getLeagueByeWeekTeamsLoaded = (state: RootState): boolean =>
  state.league.storesLoadedState.byeWeekTeamsLoaded;
export const isByeWeekTeamsEmpty = (state: RootState): boolean => state.league.byeWeekTeams.length === 0;
// #endregion

export const getTeamByName = (state: RootState, teamName: string): Maybe<ITeamDetails> => {
  const teams = getTeams(state);
  return teams.find((team) => team.teamAbrv.toUpperCase() === teamName.toUpperCase());
};

export const getByeWeeks = (state: RootState): ITeamsOnByeWeek[] => state.league.byeWeekTeams;
export const getByeWeeksFor2023 = (state: RootState): ITeamsOnByeWeek[] =>
  state.league.byeWeekTeams.filter((bwt) => bwt.year === '2023');
export const getByeWeeksFor2024 = (state: RootState): ITeamsOnByeWeek[] =>
  state.league.byeWeekTeams.filter((bwt) => bwt.year === '2024');
export const getByeWeeksFor2025 = (state: RootState): ITeamsOnByeWeek[] =>
  state.league.byeWeekTeams.filter((bwt) => bwt.year === '2025');
export const getByeWeeksForSeasonYear = (state: RootState, year: SeasonID): ITeamsOnByeWeek[] => {
  return state.league.byeWeekTeams.filter((byeWeek) => byeWeek.year === year);
};

export const getByeWeekForSelectedWeekID = memoize((state: RootState): Maybe<ITeamsOnByeWeek> => {
  const selectedSeasonWeekID = getSelectedSeasonWeek(state);
  const { yearID } = breakSeasonWeekID(selectedSeasonWeekID);
  return getByeWeeksForSeasonYear(state, yearID).find((byeWeek) => byeWeek.seasonWeekID === selectedSeasonWeekID);
});

export const getByeWeekTeamNames = memoize((state: RootState) => {
  const byeWeekTeams: Maybe<ITeamsOnByeWeek> = getByeWeekForSelectedWeekID(state);
  return byeWeekTeams?.teamIDs?.map((t) => TeamNamesByID[parseInt(t)]);
});

export const getAfcConference = (state: RootState): ITeamDetails[] => state.league.afcConference;
export const getNfcConference = (state: RootState): ITeamDetails[] => state.league.nfcConference;
export const getAfcNorth = (state: RootState): ITeamDetails[] => state.league.afcNorth;
export const getAfcEast = (state: RootState): ITeamDetails[] => state.league.afcEast;
export const getAfcWest = (state: RootState): ITeamDetails[] => state.league.afcWest;
export const getAfcSouth = (state: RootState): ITeamDetails[] => state.league.afcSouth;
export const getNfcNorth = (state: RootState): ITeamDetails[] => state.league.nfcNorth;
export const getNfcEast = (state: RootState): ITeamDetails[] => state.league.nfcEast;
export const getNfcWest = (state: RootState): ITeamDetails[] => state.league.nfcWest;
export const getNfcSouth = (state: RootState): ITeamDetails[] => state.league.nfcSouth;

export const getDivisionTeams = (state: RootState, division: NFLDivision): ITeamDetails[] => {
  switch (division) {
    case NFLDivision.AFCNorth:
      return getAfcNorth(state);
    case NFLDivision.AFCEast:
      return getAfcEast(state);
    case NFLDivision.AFCSouth:
      return getAfcSouth(state);
    case NFLDivision.AFCWest:
      return getAfcWest(state);
    case NFLDivision.NFCNorth:
      return getNfcNorth(state);
    case NFLDivision.NFCEast:
      return getNfcEast(state);
    case NFLDivision.NFCSouth:
      return getNfcSouth(state);
    case NFLDivision.NFCWest:
      return getNfcWest(state);
    default:
      console.warn(`Should not reach this state: ${division}`);
      return [];
  }
};

export const getSelectedTeamDetails = (state: RootState): ITeamDetails => {
  const teams = getTeams(state);
  const teamName = getSelectedTeamName(state);
  const id = TeamAbrvToTeamID[teamName as TeamAbrvType];
  return teams[parseInt(id) - 1];
};

export const getUIDScheduleByTeamID = (state: RootState, teamID: TeamID, year?: number): UID[] => {
  const teams = getTeams(state);
  const selectedYear = getSelectedYear(state);
  const team: Maybe<ITeamDetails> = teams.find((team) => team.teamID === teamID);
  const yr = year ?? selectedYear;
  return team ? team.scheduleByYear[yr] : [];
};

const EMPTY_TEAM_SCHEDULE: UID[] = [];

export const getUIDScheduleForSelectedTeam = (state: RootState, year?: number): UID[] => {
  const teams = getTeams(state);
  const teamName = getSelectedTeamName(state);
  const selectedYear = getSelectedYear(state);
  const teamID = TeamAbrvToTeamID[teamName as TeamAbrvType];
  const yr = year ?? selectedYear;
  const team: Maybe<ITeamDetails> = teams.find((team) => team.teamID === teamID);
  return team?.scheduleByYear?.[yr] ?? EMPTY_TEAM_SCHEDULE;
};

export const getWeeksPlayedByTeamID = (state: RootState, teamID: TeamID, year?: number): number => {
  const teams = getTeams(state);
  const selectedWeekID = getSelectedWeek(state);
  const selectedYear = getSelectedYear(state);

  if (teams.length !== NFL_TEAMS_TOTAL) return 1;

  const team: Maybe<ITeamDetails> = teams.find((team) => team.teamID === teamID);
  if (team) {
    const selectedWeekNum = toNumber(selectedWeekID);
    const yr = year ?? selectedYear;
    const byeWeekIndex = team.scheduleByYear[yr].findIndex((uid) => uid.endsWith(BYE));
    return byeWeekIndex > selectedWeekNum ? selectedWeekNum : selectedWeekNum - 1;
  }

  return 1;
};
// eslint:disable-next-line: max-line-length
export const getAllTeamsGamesPlayedBySelectedWeek = memoize((state: RootState, year?: number): number[] => {
  const teams = getTeams(state);
  const selectedWeek = getSelectedWeek(state);
  // const selectedYear = getSelectedYear(state);
  if (CONSOLE_ON) console.log(`getAllTeamsGamesPlayedBySelectedWeek for year ${year}`);
  const weekNum = toNumber(selectedWeek);
  // const yr = year ?? selectedYear;
  return teams.map(() => {
    // const byeWeekIndex = team.scheduleByYear[yr].findIndex((uid) => uid.endsWith(BYE));
    return weekNum;
    // return byeWeekIndex > weekNum ? weekNum : weekNum - 1;
  });
});

export default leagueSlice.reducer;
